<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Alertas de Performance',
  },
});

const alerts = computed(() => props.data?.alerts || []);

function getAlertIcon(type) {
  const icons = {
    critical: 'i-lucide-alert-octagon',
    warning: 'i-lucide-alert-triangle',
    info: 'i-lucide-info',
  };
  return icons[type] || icons.info;
}

function getAlertColor(type) {
  const colors = {
    critical: 'text-n-ruby-11 bg-n-ruby-3',
    warning: 'text-n-amber-11 bg-n-amber-3',
    info: 'text-n-blue-11 bg-n-blue-3',
  };
  return colors[type] || colors.info;
}

function getCategoryIcon(category) {
  const icons = {
    csat: 'i-lucide-star',
    sla: 'i-lucide-shield-check',
    response_time: 'i-lucide-timer',
    resolution: 'i-lucide-check-circle',
    bot: 'i-lucide-bot',
    workload: 'i-lucide-users',
  };
  return icons[category] || 'i-lucide-bell';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-bell text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="data.critical"
          class="px-2 py-0.5 text-xs rounded bg-n-ruby-3 text-n-ruby-11"
        >
          {{ data.critical }} criticos
        </span>
        <span
          v-if="data.warnings"
          class="px-2 py-0.5 text-xs rounded bg-n-amber-3 text-n-amber-11"
        >
          {{ data.warnings }} avisos
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-32">
      <Spinner size="large" />
    </div>

    <!-- No Alerts -->
    <div
      v-else-if="alerts.length === 0"
      class="flex flex-col items-center justify-center h-32 text-n-teal-11"
    >
      <span class="i-lucide-check-circle-2 text-4xl mb-2" />
      <span class="text-sm font-medium">Tudo em ordem!</span>
      <span class="text-xs text-n-slate-11">Nenhum alerta de performance</span>
    </div>

    <!-- Alerts List -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="(alert, idx) in alerts"
        :key="idx"
        class="flex items-start gap-3 p-3 rounded-lg"
        :class="getAlertColor(alert.type)"
      >
        <span class="text-lg mt-0.5" :class="[getAlertIcon(alert.type)]" />
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span :class="getCategoryIcon(alert.category)" class="text-sm" />
            <span class="text-sm font-medium">{{ alert.message }}</span>
          </div>
          <div class="flex items-center gap-3 mt-1 text-xs opacity-80">
            <span>Valor: {{ alert.value }}</span>
            <span>Limite: {{ alert.threshold }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div
      v-if="data.total_alerts > 0"
      class="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-n-weak text-xs text-n-slate-11"
    >
      <span>{{ data.total_alerts }} alertas no total</span>
    </div>
  </div>
</template>
